import React, { useState } from 'react';
import { View } from 'react-native';
import { translate } from '../../services/translate';
import { WindowInformation } from '../../services/window-information';
import FormInput from '../FormInput';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyDateInput from '../MyDateInput';
import GroupContainer from '../GroupContainer';
import FilterSwitch from '../Filter/components/FilterSwitch';
import ProvidenceDropdown from './ProvidenceDropdown';
import HandcuffReasonDropdown from './HandcuffReasonDropdown';
import PersonRelationDropdown from './PersonRelationDropdown';
import OccurrenceTypeDropdown from './OccurrenceTypeDropdown';
import MyPhoneInput from '../MyPhoneInput';
import FilterDropdown from '../Filter/components/FilterDropdown';
import BasicModal from '../BasicModal';

interface InvolvedPersonModalParameters {
    involvedPerson: InvolvedPerson;
    setInvolvedPerson: (value: InvolvedPerson) => void;
    isModalVisible: boolean;
    setModalVisible: (value: boolean) => void;
    onSubmit: () => void;
}

export default function InvolvedPersonModal({ involvedPerson, setInvolvedPerson, isModalVisible, setModalVisible, onSubmit }: InvolvedPersonModalParameters) {
    const { styles } = useStyles(stylesheet);
    const windowInfo = WindowInformation();

    const [isBirthInvalid, setIsBirthInvalid] = useState<boolean>(false);

    function isValidForm() {
        if (involvedPerson.name === '') {
            return false;
        }
        if (involvedPerson.motherName === '') {
            return false;
        }
        if (involvedPerson.birth === '' || isBirthInvalid) {
            return false;
        }
        if (involvedPerson.document === '') {
            return false;
        }
        return true;
    }

    return (
        <BasicModal
            isModalVisible={isModalVisible}
            setModalVisible={setModalVisible}
            title={translate('involvedPerson')}
            width='extra-large'
            primaryButton={{
                text: translate('save'),
                onClick: onSubmit,
                disabled: !isValidForm(),
            }}
        >
            <View style={{ rowGap: 20 }}>
                <View style={styles.row}>
                    <FormInput
                        label={translate('name')}
                        placeholder={translate('name')}
                        value={involvedPerson.name}
                        onChangeText={(value) => setInvolvedPerson({ ...involvedPerson, name: value })}
                        invalid={() => false}
                        viewStyle={windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }}
                        required={true}
                    />
                    <FormInput
                        label={translate('motherName')}
                        placeholder={translate('motherName')}
                        value={involvedPerson.motherName}
                        onChangeText={(value) => setInvolvedPerson({ ...involvedPerson, motherName: value })}
                        invalid={() => false}
                        viewStyle={windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }}
                        required={true}
                    />
                    <View style={[{ rowGap: 5 }, windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }]}>
                        <MyDateInput
                            label={translate('birth')}
                            labelFontSize={16}
                            date={involvedPerson.birth}
                            onChangeDate={(text: string) => {
                                setInvolvedPerson({ ...involvedPerson, birth: text });
                            }}
                            hasMaxWidth={false}
                            required={true}
                            allowDateBiggerThanToday={false}
                            isInvalid={isBirthInvalid}
                            setIsInvalid={setIsBirthInvalid}
                        />
                    </View>
                    <View style={[{ gap: 5, zIndex: 13 }, windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }]}>
                        <FilterDropdown
                            value={involvedPerson.gender}
                            setValue={(value) => {
                                setInvolvedPerson({ ...involvedPerson, gender: value(involvedPerson.gender) });
                            }}
                            items={['male', 'female'].map((item: string) => {
                                return { label: translate(item), value: item };
                            })}
                            label={translate('gender')}
                            searchable={false}
                        />
                    </View>
                    <View style={[{ gap: 5, zIndex: 11 }, windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }]}>
                        <PersonRelationDropdown
                            value={involvedPerson.personRelationId}
                            setValue={(value, item) => {
                                setInvolvedPerson({
                                    ...involvedPerson,
                                    personRelationId: value,
                                    personRelation: item
                                });
                            }}
                            zIndex={10}
                        />
                    </View>
                    <FormInput
                        label={translate('arrestWarrantNumber')}
                        placeholder={translate('arrestWarrantNumber')}
                        value={involvedPerson.arrestWarrantNumber || ''}
                        onChangeText={(value) => setInvolvedPerson({ ...involvedPerson, arrestWarrantNumber: value })}
                        invalid={() => false}
                        viewStyle={windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }}
                    />
                    <View style={[{ gap: 5, zIndex: 9 }, windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }]}>
                        <ProvidenceDropdown
                            setValue={(value) => {
                                setInvolvedPerson({ ...involvedPerson, providenceId: value(involvedPerson.providenceId) });
                            }}
                            value={involvedPerson.providenceId}
                        />
                    </View>
                    <FormInput
                        label={translate('address')}
                        placeholder={translate('address')}
                        value={involvedPerson.address}
                        onChangeText={(value) => setInvolvedPerson({ ...involvedPerson, address: value })}
                        invalid={() => false}
                        viewStyle={windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }}
                    />
                    <FormInput
                        label={translate('neighborhood')}
                        placeholder={translate('neighborhood')}
                        value={involvedPerson.neighborhood}
                        onChangeText={(value) => setInvolvedPerson({ ...involvedPerson, neighborhood: value })}
                        invalid={() => false}
                        viewStyle={windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }}
                    />

                    <MyPhoneInput
                        label={translate('phone')}
                        placeholder={translate('phone')}
                        value={involvedPerson.phoneNumber}
                        onChangeText={(value) => setInvolvedPerson({ ...involvedPerson, phoneNumber: value })}
                        invalid={() => false}
                        viewStyle={windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }}
                        labelStyle={{
                            fontSize: 16
                        }}
                    />
                </View>

                <View style={[styles.row, { zIndex: 8 }]}>
                    <View style={[{ gap: 5 }, windowInfo.isMobileOrTablet ? { width: '100%' } : {}]}>
                        <FilterSwitch
                            value={involvedPerson.arrested}
                            label={translate('arrested')}
                            onChange={(value) => {
                                setInvolvedPerson({ ...involvedPerson, arrested: Boolean(value) });
                            }}
                            direction='column'
                        />
                    </View>
                    {involvedPerson.arrested ? <>
                        <FormInput
                            label={translate('policeReport')}
                            placeholder={translate('policeReport')}
                            value={involvedPerson.policeReport || ''}
                            onChangeText={(value) => setInvolvedPerson({ ...involvedPerson, policeReport: value })}
                            invalid={() => false}
                            viewStyle={windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }}
                        />
                        <View style={[{ gap: 5, zIndex: 7 }, windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }]}>
                            <OccurrenceTypeDropdown
                                value={involvedPerson.policeReportNatureId || 0}
                                setValue={(value) => {
                                    setInvolvedPerson({ ...involvedPerson, policeReportNatureId: value(-1) });
                                }}
                                zIndex={6}
                            />
                        </View></> : null
                    }
                    <View style={[{ gap: 5 }, windowInfo.isMobileOrTablet ? { width: '100%' } : {}]}>
                        <FilterSwitch
                            value={involvedPerson.handcuffUsed}
                            label={translate('handcuffUsed')}
                            onChange={(value) => {
                                setInvolvedPerson({ ...involvedPerson, handcuffUsed: Boolean(value) });
                            }}
                            direction='column'
                        />
                    </View>
                    {involvedPerson.handcuffUsed ?
                        <View style={[{
                            gap: 5, zIndex: 5
                        }, windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }]}>
                            <HandcuffReasonDropdown
                                setValue={(value) => {
                                    setInvolvedPerson({ ...involvedPerson, handcuffReasonId: value(-1) });
                                }}
                                value={involvedPerson.handcuffReasonId || 0}
                                zIndex={4}
                            />
                        </View> : null
                    }
                </View>

                <GroupContainer label={translate('document')}>
                    <View style={[styles.row, { zIndex: 3 }]}>
                        <View style={[{ gap: 5, zIndex: 2 }, windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }]}>
                            <FilterDropdown
                                value={involvedPerson.documentType}
                                setValue={(value) => {
                                    setInvolvedPerson({ ...involvedPerson, documentType: value(involvedPerson.documentType) });
                                }}
                                items={['rg', 'cpf'].map((item: string) => {
                                    return {
                                        label: item.toUpperCase(), value: item
                                    };
                                })}
                                label={translate('type')}
                                searchable={false}
                            />
                        </View>
                        <FormInput
                            label={translate('number')}
                            placeholder={translate('number')}
                            value={involvedPerson.document}
                            onChangeText={(value) => setInvolvedPerson({ ...involvedPerson, document: value })}
                            invalid={() => false}
                            viewStyle={windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }}
                            required={true}
                        />
                        <FormInput
                            label={translate('series')}
                            placeholder={translate('series')}
                            value={involvedPerson.documentSeries}
                            onChangeText={(value) => setInvolvedPerson({ ...involvedPerson, documentSeries: value })}
                            invalid={() => false}
                            viewStyle={windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }}
                        />
                        <FormInput
                            label={translate('publicBody')}
                            placeholder={translate('publicBody')}
                            value={involvedPerson.publicBody || ''}
                            onChangeText={(value) => setInvolvedPerson({ ...involvedPerson, publicBody: value })}
                            invalid={() => false}
                            viewStyle={windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }}
                        />
                        <FormInput
                            label={translate('federalUnit')}
                            placeholder={translate('federalUnit')}
                            value={involvedPerson.documentState || ''}
                            onChangeText={(value) => setInvolvedPerson({ ...involvedPerson, documentState: value })}
                            invalid={() => false}
                            viewStyle={windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }}
                        />
                    </View>

                </GroupContainer>

                <GroupContainer label={translate('companyOrActingBody')}>
                    <View style={[styles.row]}>
                        <FormInput
                            label={translate('name')}
                            placeholder={translate('name')}
                            value={involvedPerson.company}
                            onChangeText={(value) => setInvolvedPerson({ ...involvedPerson, company: value })}
                            invalid={() => false}
                            viewStyle={windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }}
                        />
                        <FormInput
                            label={translate('role')}
                            placeholder={translate('role')}
                            value={involvedPerson.companyRole}
                            onChangeText={(value) => setInvolvedPerson({ ...involvedPerson, companyRole: value })}
                            invalid={() => false}
                            viewStyle={windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }}
                        />
                        <FormInput
                            label={translate('address')}
                            placeholder={translate('address')}
                            value={involvedPerson.companyAddress}
                            onChangeText={(value) => setInvolvedPerson({ ...involvedPerson, companyAddress: value })}
                            invalid={() => false}
                            viewStyle={windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }}
                        />
                        <FormInput
                            label={translate('neighborhood')}
                            placeholder={translate('neighborhood')}
                            value={involvedPerson.companyNeighborhood}
                            onChangeText={(value) => setInvolvedPerson({ ...involvedPerson, companyNeighborhood: value })}
                            invalid={() => false}
                            viewStyle={windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }}
                        />
                        <MyPhoneInput
                            label={translate('phone')}
                            placeholder={translate('phone')}
                            value={involvedPerson.companyPhoneNumber}
                            onChangeText={(value) => setInvolvedPerson({ ...involvedPerson, companyPhoneNumber: value })}
                            invalid={() => false}
                            viewStyle={windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }}
                            labelStyle={{
                                fontSize: 16
                            }}
                        />
                    </View>

                </GroupContainer>

                <GroupContainer label={translate('otherUsedEquipments')}>
                    <View style={styles.row}>
                        <View style={[windowInfo.isMobileOrTablet ? { width: '100%' } : {}]}>
                            <FilterSwitch
                                value={involvedPerson.usedFireGun}
                                label={translate('fireGun')}
                                onChange={(value) => {
                                    setInvolvedPerson({ ...involvedPerson, usedFireGun: Boolean(value) });
                                }}
                                direction='column'
                            />
                        </View>
                        <View style={[windowInfo.isMobileOrTablet ? { width: '100%' } : {}]}>
                            <FilterSwitch
                                value={involvedPerson.usedBat}
                                label={translate('bat')}
                                onChange={(value) => {
                                    setInvolvedPerson({ ...involvedPerson, usedBat: Boolean(value) });
                                }}
                                direction='column'
                            />
                        </View>
                        <View style={[windowInfo.isMobileOrTablet ? { width: '100%' } : {}]}>
                            <FilterSwitch
                                value={involvedPerson.usedElectricGun}
                                label={translate('electricGun')}
                                onChange={(value) => {
                                    setInvolvedPerson({ ...involvedPerson, usedElectricGun: Boolean(value) });
                                }}
                                direction='column'
                            />
                        </View>
                        <View style={[windowInfo.isMobileOrTablet ? { width: '100%' } : {}]}>
                            <FilterSwitch
                                value={involvedPerson.usedSpreaderGas}
                                label={translate('spreaderGas')}
                                onChange={(value) => {
                                    setInvolvedPerson({ ...involvedPerson, usedSpreaderGas: Boolean(value) });
                                }}
                                direction='column'
                            />
                        </View>
                    </View>
                </GroupContainer>

                <GroupContainer label={translate('movedTo')}>
                    <View style={styles.row}>
                        <View style={[windowInfo.isMobileOrTablet ? { width: '100%' } : {}]}>
                            <FilterSwitch
                                value={involvedPerson.movedToFirstAidStation}
                                label={translate('firstAidStation')}
                                onChange={(value) => {
                                    setInvolvedPerson({ ...involvedPerson, movedToFirstAidStation: Boolean(value) });
                                }}
                                direction='column'
                            />
                        </View>
                        <View style={[windowInfo.isMobileOrTablet ? { width: '100%' } : {}]}>
                            <FilterSwitch
                                value={involvedPerson.movedToHospital}
                                label={translate('hospital')}
                                onChange={(value) => {
                                    setInvolvedPerson({ ...involvedPerson, movedToHospital: Boolean(value) });
                                }}
                                direction='column'
                            />
                        </View>
                        <View style={[windowInfo.isMobileOrTablet ? { width: '100%' } : {}]}>
                            <FilterSwitch
                                value={involvedPerson.movedToLegalMedicalInstitute}
                                label={translate('legalMedicalInstitute')}
                                onChange={(value) => {
                                    setInvolvedPerson({ ...involvedPerson, movedToLegalMedicalInstitute: Boolean(value) });
                                }}
                                direction='column'
                            />
                        </View>
                        <View style={[windowInfo.isMobileOrTablet ? { width: '100%' } : {}]}>
                            <FilterSwitch
                                value={involvedPerson.movedToPoliceStation}
                                label={translate('policeStation')}
                                onChange={(value) => {
                                    setInvolvedPerson({ ...involvedPerson, movedToPoliceStation: Boolean(value) });
                                }}
                                direction='column'
                            />
                        </View>
                    </View>
                </GroupContainer>
            </View>
        </BasicModal>
    );
}


const stylesheet = createStyleSheet(() => ({
    row: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        columnGap: 30,
        minHeight: 'auto',
        rowGap: 20,
    },
}));
